import { canMenu } from "../lib/menuPermissions";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin, Building2, Pencil, Trash2, Loader2, Mail, Link2, Unlink, CalendarDays, Users, Tag, FolderOpen, Phone, Calculator, ExternalLink, LocateFixed, Sun, Cloud, CloudRain, CloudLightning, Wind, Droplets } from "lucide-react";
import { toast } from "sonner";
import { api, euro } from "../lib/api";
import { SiteFormModal, SITE_STATUS } from "../components/cantieri/SiteFormModal";
import { ToolsPanel } from "../components/cantieri/ToolsPanel";
import { JournalPanel } from "../components/cantieri/JournalPanel";
import { DocumentsPanel } from "../components/cantieri/DocumentsPanel";
import { AccountingDashboard } from "../components/cantieri/AccountingDashboard";
import { AttendancePanel, PayrollCard } from "../components/cantieri/AttendancePanel";
import { TaskList } from "../components/cantieri/TaskList";
import { WeekAgenda } from "../components/cantieri/WeekAgenda";
import { EmailCard } from "../components/cantieri/EmailCard";
import { GanttChart } from "../components/cantieri/GanttChart";
import { SalPanel } from "../components/cantieri/SalPanel";
import { QuickCostsList } from "../components/cantieri/QuickCostsList";
import CmeTab from "../components/tabs/CmeTab";
import BimViewerTab from "../components/tabs/BimViewerTab";

const TABS = [["operativa", "Operativo"], ["contabile", "Contabile"], ["files", "Documenti"], ["info", "Dati cantiere"]];
const SUB_OP = [["dashboard", "Cruscotto"], ["tasks", "Task"], ["fasi", "Fasi"], ["bimviewer", "BIM Viewer"], ["gantt", "Cronoprogramma"], ["attendance", "Presenze"], ["tools", "Tool operativi"], ["journal", "Giornale lavori"], ["emails", "Email"]];
const SUB_CONT = [["dashboard", "Cruscotto"], ["cme", "Computo (CME)"], ["sal", "SAL"], ["movimenti", "DDT / Fatture / Costi"], ["payroll", "Manodopera"], ["tools", "Tool contabili"], ["offers", "Offerte"]];

const WeatherIcon = ({ code, className = "w-6 h-6" }) => code >= 95 ? <CloudLightning className={className}/> : code >= 51 ? <CloudRain className={className}/> : code >= 2 ? <Cloud className={className}/> : <Sun className={className}/>;

const WeatherCard = ({ siteId, site }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.siteWeather(siteId).then((r) => { if (mounted) setWeather(r); }).catch(() => { if (mounted) setWeather(null); }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [siteId, site?.latitude, site?.longitude]);
  if (loading) return <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin"/> Caricamento meteo…</div></div>;
  if (!weather?.available) return <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="flex items-center gap-2 font-semibold text-slate-800"><Cloud className="w-5 h-5 text-slate-400"/> Meteo cantiere</div><div className="text-sm text-slate-500 mt-2">{weather?.reason || "Meteo non disponibile"}</div></div>;
  const c = weather.current || {};
  const days = (weather.days || []).slice(0, 4);
  return <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
      <div className="flex items-center gap-3 lg:min-w-[250px]">
        <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><WeatherIcon code={c.weather_code} className="w-7 h-7"/></div>
        <div>
          <div className="text-xs text-slate-500">Meteo cantiere</div>
          <div className="text-3xl font-bold text-slate-900">{c.temperature_2m != null ? Math.round(c.temperature_2m) + "°" : "—"}</div>
          <div className="text-xs text-slate-500 truncate max-w-[260px]">{weather.resolved_name || site?.address || ""}</div>
        </div>
      </div>
      <div className="flex gap-5 text-sm text-slate-600">
        <div className="inline-flex items-center gap-1.5"><Droplets className="w-4 h-4 text-sky-500"/> Pioggia {c.precipitation ?? 0} mm</div>
        <div className="inline-flex items-center gap-1.5"><Wind className="w-4 h-4 text-slate-500"/> {c.wind_speed_10m != null ? Math.round(c.wind_speed_10m) + " km/h" : "—"}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:ml-auto flex-1 max-w-2xl">
        {days.map((d) => <div key={d.date} className="rounded-lg bg-slate-50 px-3 py-2 text-center">
          <div className="text-[11px] text-slate-500">{new Date(d.date + "T12:00:00").toLocaleDateString("it-IT",{weekday:"short"})}</div>
          <WeatherIcon code={d.weather_code} className="w-5 h-5 mx-auto my-1 text-sky-600"/>
          <div className="text-xs font-semibold">{d.temp_min != null ? Math.round(d.temp_min) : "—"}° / {d.temp_max != null ? Math.round(d.temp_max) : "—"}°</div>
          <div className="text-[10px] text-sky-700 mt-1">Pioggia {d.precip_probability ?? 0}%</div>
        </div>)}
      </div>
    </div>
  </div>;
};

const SitePhasesPanel = ({ site }) => {
  const offers = site.offers || [];
  const [offerId, setOfferId] = useState(offers[0]?.id || "");
  const [items, setItems] = useState([]);
  const [verificationRows, setVerificationRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ids = offers.map((o) => o.id);
    if (!ids.length) {
      setOfferId("");
      setItems([]);
      return;
    }
    if (!offerId || !ids.includes(offerId)) setOfferId(ids[0]);
  }, [site.id, offers, offerId]);

  const loadPhases = useCallback(async () => {
    if (!offerId) { setItems([]); return; }
    setLoading(true);
    try {
      const [cme, verification] = await Promise.all([
        api.getCme(offerId),
        api.takeoffBimCmeVerification(offerId).catch(() => null),
      ]);
      setItems(cme.items || []);
      setVerificationRows(verification?.rows || []);
    } catch {
      setItems([]);
      setVerificationRows([]);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => { loadPhases(); }, [loadPhases]);

  const openPdf = async (fileId) => {
    if (!fileId) return;
    const popup = window.open("", "_blank");
    try {
      const blob = await api.fileBlob(fileId);
      const url = URL.createObjectURL(blob);
      if (popup) popup.location = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (popup) popup.close();
      toast.error(e?.response?.data?.detail || "Impossibile aprire la tavola PDF");
    }
  };

  const pdfLinksFor = (itemId) => {
    const row = verificationRows.find((v) => (v.cme_ids || (v.cme_id ? [v.cme_id] : [])).includes(itemId));
    if (!row) return [];
    const links = [...(row.drawing_evidence || []), ...(row.pdf_quantity_sources || [])]
      .filter((x) => x?.file_id)
      .map((x) => ({ file_id: x.file_id, filename: x.filename || "Tavola PDF" }));
    const seen = new Set();
    return links.filter((x) => {
      const key = x.file_id + "|" + x.filename;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const progressFor = (item) => Number(item.progress_pct ?? (item.done ? 100 : 0));
  const setProgress = async (item, value) => {
    if (String(value).trim() === "") return toast.error("Inserisci una percentuale");
    const percentage = Number(value);
    if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) return toast.error("Percentuale valida: 0-100");
    if (percentage === progressFor(item)) return;
    const payload = {
      wbs: item.wbs || "",
      category: item.category || "",
      subcategory: item.subcategory || "",
      code: item.code || "",
      description: item.description || "",
      unit: item.unit || "",
      quantity: item.quantity || 0,
      unit_price: item.unit_price || 0,
      labor_pct: item.labor_pct || 0,
      done: percentage === 100,
      progress_pct: percentage,
    };
    try {
      const updated = await api.updateCme(item.id, payload);
      setItems((rows) => rows.map((x) => x.id === item.id ? { ...x, ...updated } : x));
      toast.success("Avanzamento aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fase non riuscito");
    }
  };

  if (!offers.length) {
    return <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-400">Nessuna offerta collegata al cantiere. Collega un'offerta per generare le fasi dal computo metrico.</div>;
  }

  const total = items.length;
  const completed = items.filter((x) => progressFor(x) === 100).length;
  const pct = total ? Math.round(items.reduce((sum, x) => sum + progressFor(x), 0) / total) : 0;
  const groups = [];
  const grouped = {};
  items.forEach((it) => {
    const cat = (it.category || "").trim() || "Senza categoria";
    if (!grouped[cat]) { grouped[cat] = []; groups.push(cat); }
    grouped[cat].push(it);
  });

  return <div className="space-y-4">
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1">
        <div className="text-xs text-slate-500">Avanzamento fasi</div>
        <div className="flex items-end gap-2 mt-1"><span className="text-2xl font-bold text-slate-900">{pct}%</span><span className="text-xs text-slate-500 pb-1">{completed}/{total} voci completate</span></div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{width: pct+"%"}}/></div>
      </div>
      {offers.length > 1 && <select value={offerId} onChange={(e) => setOfferId(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
        {offers.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
      </select>}
    </div>

    {loading ? <div className="bg-white border border-slate-200 rounded-xl p-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-orange-500 mx-auto"/></div> :
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-400">
            <th className="px-3 py-2.5 text-center w-16">Avanzamento</th>
            <th className="px-3 py-2.5 text-left w-20">WBS</th>
            <th className="px-3 py-2.5 text-left w-28">Codice</th>
            <th className="px-3 py-2.5 text-left">Descrizione</th>
            <th className="px-3 py-2.5 text-left w-16">U.M.</th>
            <th className="px-3 py-2.5 text-right w-24">Q.tà</th>
            <th className="px-3 py-2.5 text-left w-48">Tavole PDF</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {!items.length ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Nessuna voce nel computo metrico.</td></tr> :
              groups.map((cat) => <Fragment key={"phase-"+cat}>
                <tr className="bg-orange-50/70"><td colSpan={7} className="px-3 py-2 font-display font-semibold text-orange-700">▸ {cat}</td></tr>
                {grouped[cat].map((it) => <tr key={it.id} className={it.done ? "bg-emerald-50/40 text-slate-500" : "hover:bg-slate-50"}>
                  <td className="px-3 py-2 text-center"><div className="flex items-center justify-center gap-1">
                    <input key={it.id + "-" + progressFor(it)} type="number" min="0" max="100" step="1" defaultValue={progressFor(it)} onBlur={(e) => setProgress(it, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} aria-label={"Avanzamento " + (it.code || it.id)} className="w-16 border border-slate-200 rounded px-1 py-1 text-center text-xs" />
                    <span className="text-xs">%</span></div></td>
                  <td className="px-3 py-2 font-mono text-xs">{it.wbs || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.code || "—"}</td>
                  <td className="px-3 py-2"><div className={it.done ? "line-through decoration-slate-300" : ""}>{it.description || "—"}</div>{it.done_at && <div className="text-[10px] text-emerald-700 mt-0.5">Eseguita {String(it.done_at).slice(0,10)}</div>}</td>
                  <td className="px-3 py-2 text-xs">{it.unit || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{Number(it.quantity || 0).toLocaleString("it-IT",{maximumFractionDigits:3})}</td>
                  <td className="px-3 py-2">
                    {pdfLinksFor(it.id).length ? <div className="space-y-1">
                      {pdfLinksFor(it.id).map((pdf, i) => <button key={pdf.file_id+"-"+i} type="button" onClick={() => openPdf(pdf.file_id)} className="w-full text-left inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 hover:text-blue-900 hover:underline">
                        <ExternalLink className="w-3 h-3 shrink-0"/><span className="truncate">{pdf.filename}</span>
                      </button>)}
                    </div> : <span className="text-[10px] text-slate-300">—</span>}
                  </td>
                </tr>)}
              </Fragment>)
            }
          </tbody>
        </table>
      </div>
    </div>}
  </div>;
};

const SiteBimViewerPanel = ({ site }) => {
  const offers = site.offers || [];
  const [offerId, setOfferId] = useState(offers[0]?.id || "");
  useEffect(() => {
    if (!offers.some((o) => o.id === offerId)) setOfferId(offers[0]?.id || "");
  }, [offers, offerId]);
  if (!offers.length) return <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-500">Nessuna offerta collegata al cantiere. Associa un'offerta dalla sezione Contabile → Offerte per visualizzare i modelli BIM.</div>;
  return <div className="space-y-4">
    {offers.length > 1 && <label className="flex items-center gap-3 text-sm text-slate-700">Offerta
      <select aria-label="Seleziona offerta BIM" value={offerId} onChange={(e) => setOfferId(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
        {offers.map((o) => <option key={o.id} value={o.id}>{o.title || o.name || o.id}</option>)}
      </select>
    </label>}
    <BimViewerTab offerId={offerId} />
  </div>;
};

export const SiteCmePanel = ({ site }) => {
  const offers = site.offers || [];
  const [offerId, setOfferId] = useState(offers[0]?.id || "");
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    const ids = offers.map((o) => o.id);
    if (!ids.length) {
      setOfferId("");
      setOffer(null);
      return;
    }
    if (!offerId || !ids.includes(offerId)) setOfferId(ids[0]);
  }, [site.id, offers, offerId]);

  useEffect(() => {
    if (!offerId) { setOffer(null); return; }
    let mounted = true;
    api.getOffer(offerId).then((o) => { if (mounted) setOffer(o); }).catch(() => { if (mounted) setOffer(null); });
    return () => { mounted = false; };
  }, [offerId]);

  if (!offers.length) {
    return <CmeTab siteId={site.id} hideToolbar cleanView />;
  }

  return <div className="space-y-4">
    {offers.length > 1 && <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <label className="text-xs font-semibold text-slate-500">Computo offerta</label>
      <select value={offerId} onChange={(e) => setOfferId(e.target.value)} className="flex-1 max-w-xl px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
        {offers.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
      </select>
    </div>}
    {offer ? <CmeTab offerId={offerId} offer={offer} hideToolbar cleanView /> : <div className="bg-white border border-slate-200 rounded-xl p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-orange-500 mx-auto"/></div>}
  </div>;
};

const OffersPanel = ({ site, reload }) => {
  const [all, setAll] = useState([]);
  const [pick, setPick] = useState("");
  useEffect(() => { api.listOffers().then(setAll).catch(() => {}); }, []);
  const linked = new Set(site.offer_ids || []);
  const free = all.filter((o) => !linked.has(o.id));
  const link = async () => { if (!pick) return; await api.linkOffer(site.id, pick); setPick(""); toast.success("Offerta associata"); reload(); };
  const unlink = async (oid) => { await api.unlinkOffer(site.id, oid); reload(); };
  return (
    <div className="space-y-4">
      <div className="flex gap-2 bg-white border border-slate-200 rounded-xl p-3">
        <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="link-offer-select" className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
          <option value="">Seleziona un'offerta da associare…</option>
          {free.map((o) => <option key={o.id} value={o.id}>{o.title} — {o.client_name || o.client_type}{o.site_id ? " (già su altro cantiere)" : ""}</option>)}
        </select>
        <button onClick={link} disabled={!pick} data-testid="link-offer-button" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold disabled:opacity-50"><Link2 className="w-4 h-4" /> Associa</button>
      </div>
      {(site.offers || []).length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Nessuna offerta associata.</p> : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {site.offers.map((o) => (
            <div key={o.id} data-testid={`site-offer-${o.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <Link to={`/offerte/${o.id}`} className="text-sm font-medium text-slate-800 hover:text-orange-600">{o.title}</Link>
                <div className="text-xs text-slate-400 capitalize">{o.client_type} · {o.region} · {o.status}</div>
              </div>
              <button onClick={() => unlink(o.id)} data-testid={`unlink-offer-${o.id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500" title="Rimuovi associazione"><Unlink className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function SiteDetail({ session }) {
  const canSiteSub = key => canMenu(session?.user,"sites",key) && (
    ["fasi","bimviewer"].includes(key) ? canMenu(session?.user,"offers","computo") :
    key === "attendance" ? canMenu(session?.user,"workers","attendance") :
    key === "emails" ? canMenu(session?.user,"emails") : true);
  const canWrite = session?.user?.is_admin || session?.user?.permissions?.write !== false;
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [site, setSite] = useState(null);
  const [tasks, setTasks] = useState([]);
  const openTasks = tasks.filter((t) => t.status !== "completato").length;
  const [emails, setEmails] = useState([]);
  const [tab, setTab] = useState("operativa");
  const [sub, setSub] = useState(() => searchParams.get("operativo") || "dashboard");
  const focusedTaskId = searchParams.get("task") || "";
  const [contSub, setContSub] = useState("dashboard");
  const [docCat, setDocCat] = useState("");
  const [edit, setEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, t, e] = await Promise.all([api.getSite(id), canMenu(session?.user,"sites","tasks") ? api.listTasks({ site_id: id }) : Promise.resolve([]), canMenu(session?.user,"sites","emails") && canMenu(session?.user,"emails") ? api.listEmails({ site_id: id }) : Promise.resolve([])]);
      setSite(s); setTasks(t); setEmails(e);
    } catch { navigate("/cantieri"); }
  }, [id, navigate, session]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const target=searchParams.get("operativo"); if(target){ setTab("operativa"); setSub(target); } }, [searchParams]);

  const remove = async () => {
    if (!window.confirm("Eliminare il cantiere? Task ed email torneranno tra le Richieste.")) return;
    await api.deleteSite(id); toast.success("Cantiere eliminato"); navigate("/cantieri");
  };

  if (!site) return <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" /></div>;
  const st = SITE_STATUS[site.status] || SITE_STATUS.attivo;

  return (
    <div className="space-y-6 min-w-0 max-w-full overflow-x-hidden">
      <Link to="/cantieri" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-sites"><ArrowLeft className="w-4 h-4" /> Cantieri</Link>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight text-slate-900 truncate" data-testid="site-title">{site.name}</h1>
            <span className={`text-[11px] px-2 py-0.5 rounded border font-semibold ${st.cls}`} data-testid="site-status-badge">{st.label}</span>
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-slate-500 mt-1">
            {site.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {site.address}</span>}
            {site.latitude != null && site.longitude != null && <a href={`https://www.google.com/maps?q=${site.latitude},${site.longitude}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange-700 hover:underline"><LocateFixed className="w-3.5 h-3.5" /> {Number(site.latitude).toFixed(5)}, {Number(site.longitude).toFixed(5)}</a>}
            {site.client_name && <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {site.client_name}</span>}
            {(site.start_date || site.end_date) && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {site.start_date || "?"} → {site.end_date || "?"}</span>}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <select disabled={!canWrite} value={site.status} onChange={async (e) => { await api.updateSite(id, { status: e.target.value }); load(); }} data-testid="site-status-select" className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
            {Object.entries(SITE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={() => setEdit(true)} data-testid="site-edit-button" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:border-slate-300"><Pencil className="w-4 h-4" /> Modifica</button>
          <button onClick={remove} data-testid="site-delete-button" className="p-2 rounded-lg border border-slate-200 text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); if (k === "operativa") setSub("dashboard"); if (k === "contabile") setContSub("dashboard"); }}
            data-testid={`site-tab-${k}`} className={`px-4 py-2.5 text-sm -mb-px border-b-2 whitespace-nowrap ${tab === k ? "border-orange-600 text-orange-700 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {l}{k === "files" && site.files > 0 ? <span className="ml-1.5 text-[11px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{site.files}</span> : null}
          </button>
        ))}
      </div>

      {tab === "operativa" && (
        <div className="space-y-4" data-testid="section-operativa">
          <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-1">
            {SUB_OP.filter(([k])=>canSiteSub(k)).map(([k, l]) => (
              <button key={k} onClick={() => setSub(k)} data-testid={`op-sub-${k}`} className={`px-3 py-1.5 text-xs font-semibold rounded-md ${sub === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{l}{k === "tasks" && openTasks > 0 ? ` (${openTasks})` : ""}</button>
            ))}
          </div>
          {canSiteSub(sub) && sub === "dashboard" && <div className="space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-500">Task aperti</div><div className="text-2xl font-bold mt-1">{openTasks}</div></div>
              <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-500">Email associate</div><div className="text-2xl font-bold mt-1">{emails.length}</div></div>
              <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-500">Documenti</div><div className="text-2xl font-bold mt-1">{site.files || 0}</div></div>
              <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-xs text-slate-500">Stato cantiere</div><div className="text-lg font-bold mt-1">{st.label}</div></div>
            </div>
            <WeatherCard siteId={id} site={site} />
            {canSiteSub("tasks") && <WeekAgenda siteId={id} refreshKey={tasks.length} title="Agenda del cantiere" />}
          </div>}
          {canSiteSub(sub) && sub === "tasks" && <TaskList tasks={tasks} onChanged={load} siteId={id} focusedTaskId={focusedTaskId} canDelete={Boolean(session?.user?.is_admin)} emptyText="Nessun task per questo cantiere. Arriveranno dalle email, dalle note vocali o aggiungili a mano." />}
          {canSiteSub(sub) && sub === "fasi" && <SitePhasesPanel site={site} />}
          {canSiteSub(sub) && sub === "bimviewer" && <SiteBimViewerPanel site={site} />}
          {canSiteSub(sub) && sub === "gantt" && <GanttChart siteId={id} site={site} />}
          {canSiteSub(sub) && sub === "attendance" && <AttendancePanel siteId={id} />}
          {canSiteSub(sub) && sub === "tools" && <ToolsPanel siteId={id} site={site} section="operativa" />}
          {canSiteSub(sub) && sub === "journal" && <JournalPanel siteId={id} site={site} onSiteChanged={load} />}
          {canSiteSub(sub) && sub === "emails" && <div className="space-y-2">{emails.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">Nessuna email associata.</p> : emails.map((e) => <EmailCard key={e.id} email={e} onChanged={load} />)}</div>}
        </div>
      )}
      {tab === "contabile" && canMenu(session?.user,"accounting") && <div className="bg-white border rounded-xl p-5 space-y-3"><h2 className="text-lg font-bold">Contabilità centralizzata</h2><p className="text-sm text-slate-600">Cruscotto, computo, SAL, DDT, fatture, manodopera e tool sono gestiti in un unico centro contabile.</p><Link to={"/contabilita-cantieri?site="+encodeURIComponent(id)} className="inline-flex bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold">Apri Contabilità cantieri</Link></div>}
      {tab === "files" && canMenu(session?.user,"documents") && <DocumentsPanel key={docCat} siteId={id} filterCategory={docCat} />}
      {tab === "info" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-5 text-sm" data-testid="site-info-panel">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2 inline-flex items-center gap-1"><Users className="w-3 h-3" /> Referenti</div>
            {(site.contacts || []).length === 0 ? <p className="text-slate-400">Nessun referente. Aggiungi nome, ruolo, società, telefono ed email per creare la rubrica del cantiere.</p> : (
              <ul className="divide-y divide-slate-100" data-testid="site-rubrica">
                {site.contacts.map((c, i) => (
                  <li key={i} className="py-2 flex flex-wrap items-center gap-2" data-testid={`rubrica-row-${i}`}>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-medium text-slate-800">{c.name || c.email || "—"}{c.role && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">{c.role}</span>}</div>
                      <div className="text-xs text-slate-500">{[c.company, c.phone, c.email].filter(Boolean).join(" · ")}</div>
                    </div>
                    {c.phone && <a href={`tel:${String(c.phone).replace(/[^+\d]/g, "")}`} data-testid={`rubrica-call-${i}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"><Phone className="w-3.5 h-3.5" /> Chiama</a>}
                    {c.email && <a href={`mailto:${c.email}`} data-testid={`rubrica-mail-${i}`} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:border-orange-400"><Mail className="w-3.5 h-3.5" /> Email</a>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Parole chiave email</div>
            <div className="flex flex-wrap gap-1.5">{(site.keywords || []).length ? site.keywords.map((k) => <span key={k} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs">{k}</span>) : <span className="text-slate-400">Nessuna</span>}</div>
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Geolocalizzazione</div>
              <button onClick={async()=>{try{await api.geocodeSite(id);toast.success("Posizione aggiornata");load()}catch(e){toast.error(e?.response?.data?.detail||"Geolocalizzazione non riuscita")}}} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold"><LocateFixed className="w-3.5 h-3.5"/> Aggiorna posizione</button>
            </div>
            {site.latitude != null && site.longitude != null ? <div className="grid md:grid-cols-[1fr_280px] gap-4 items-stretch">
              <div className="text-sm space-y-1">
                <div><span className="text-slate-400">Indirizzo:</span> {site.address || "—"}</div>
                <div><span className="text-slate-400">Posizione risolta:</span> {site.geocoded_address || "—"}</div>
                <div><span className="text-slate-400">Coordinate:</span> {Number(site.latitude).toFixed(6)}, {Number(site.longitude).toFixed(6)}</div>
                <a href={`https://www.google.com/maps?q=${site.latitude},${site.longitude}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange-700 font-semibold hover:underline"><ExternalLink className="w-3.5 h-3.5"/> Apri in Google Maps</a>
              </div>
              <div><iframe title="Mappa cantiere" className="w-full h-44 rounded-lg border border-slate-200" loading="lazy" src={`https://www.openstreetmap.org/export/embed.html?bbox=${site.longitude-0.006}%2C${site.latitude-0.004}%2C${site.longitude+0.006}%2C${site.latitude+0.004}&layer=mapnik&marker=${site.latitude}%2C${site.longitude}`}></iframe><div className="text-[10px] text-slate-400 mt-1">Mappa e geocodifica © OpenStreetMap contributors</div></div>
            </div> : <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{site.address ? "Indirizzo non ancora geolocalizzato o non riconosciuto. Usa “Aggiorna posizione” dopo aver verificato l’indirizzo." : "Inserisci l’indirizzo del cantiere per geolocalizzarlo."}</div>}
          </div>
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Note</div>
            <p className="text-slate-700 whitespace-pre-wrap">{site.notes || "—"}</p>
          </div>
        </div>
      )}

      {edit && <SiteFormModal site={site} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
    </div>
  );
}
